import React, { useState } from 'react';
import moment from 'moment';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import 'react-dates/initialize';
import { useBrsCreateStyles } from '../BrsCreateStyles';

const ISO_FORMAT = 'YYYY-MM-DD';

// react-dates / moment weekday indices: Sunday=0 ... Monday=1 ... Saturday=6.
const MONDAY = 1;
const SUNDAY = 0;

const toMoment = (value) => {
  if (!value) return null;
  const m = moment(value, ISO_FORMAT, true);
  return m.isValid() ? m : null;
};

// Whole broadcast weeks run Monday -> the following Sunday (7 days). Given a
// Monday start and a Sunday end, the inclusive day span is weeks*7 - 1, so the
// week count is (spanDays + 1) / 7.
const weeksBetween = (startStr, endStr) => {
  const start = toMoment(startStr);
  const end = toMoment(endStr);
  if (!start || !end) return '';
  const spanDays = end.diff(start, 'days');
  if (spanDays < 0) return '';
  const weeks = Math.round((spanDays + 1) / 7);
  return weeks > 0 ? String(weeks) : '';
};

const endFromStartWeeks = (startStr, weeksValue) => {
  const start = toMoment(startStr);
  const weeks = parseInt(weeksValue, 10);
  if (!start || !Number.isFinite(weeks) || weeks <= 0) return '';
  return start.clone().add(weeks * 7 - 1, 'days').format(ISO_FORMAT);
};

export const NewRequestSection = ({
  formData,
  onChange,
  inventorySources,
  onEstimateIdBlur,
  readOnly,
  showFlightWindow,
}) => {
  const classes = useBrsCreateStyles();

  // Local focus state for the two react-dates SingleDatePicker calendars.
  const [startDateFocused, setStartDateFocused] = useState(false);
  const [endDateFocused, setEndDateFocused] = useState(false);

  const handleChange = (field, value) => {
    if (readOnly) return;
    onChange({ ...formData, [field]: value });
  };

  // Flight window: Start Date (Monday only), End Date (Sunday only) and
  // Number of Weeks stay in sync. The backend persists flight_start_date +
  // flight_weeks (commit bc8f53a4); flight_end_date is a UI convenience that
  // is recomputed from the other two and never sent on the wire.
  const handleStartDateChange = (m) => {
    if (readOnly) return;
    if (m && m.day() !== MONDAY) return; // calendar only exposes Mondays
    const startStr = m ? m.format(ISO_FORMAT) : '';
    const next = { ...formData, flight_start_date: startStr };
    if (startStr && formData.flight_weeks) {
      next.flight_end_date = endFromStartWeeks(startStr, formData.flight_weeks);
    } else if (startStr && formData.flight_end_date) {
      next.flight_weeks = weeksBetween(startStr, formData.flight_end_date);
    } else if (startStr) {
      // Default to 1 broadcast week (Mon → following Sun) so the operator
      // starts with a complete week and can adjust from there.
      next.flight_weeks = '1';
      next.flight_end_date = endFromStartWeeks(startStr, '1');
    } else {
      next.flight_end_date = '';
      next.flight_weeks = '';
    }
    onChange(next);
  };

  const handleEndDateChange = (m) => {
    if (readOnly) return;
    if (m && m.day() !== SUNDAY) return; // calendar only exposes Sundays
    const endStr = m ? m.format(ISO_FORMAT) : '';
    const next = { ...formData, flight_end_date: endStr };
    if (endStr && formData.flight_start_date) {
      next.flight_weeks = weeksBetween(formData.flight_start_date, endStr);
    }
    onChange(next);
  };

  const handleWeeksChange = (rawValue) => {
    if (readOnly) return;
    const cleaned = (rawValue || '').replace(/[^0-9]/g, '');
    const next = { ...formData, flight_weeks: cleaned };
    if (cleaned && formData.flight_start_date) {
      next.flight_end_date = endFromStartWeeks(formData.flight_start_date, cleaned);
    } else if (!cleaned) {
      next.flight_end_date = '';
    }
    onChange(next);
  };

  const inputClass = readOnly
    ? `${classes.formInput} ${classes.formInputReadonly}`
    : classes.formInput;
  const selectClass = readOnly
    ? `${classes.formSelect} ${classes.formInputReadonly}`
    : classes.formSelect;

  return (
    <>
      <div className={classes.sectionHeader}>{readOnly ? 'Request' : 'New Request'}</div>
      <div className={classes.card}>
        <div className={classes.formRow}>
          <div className={classes.formGroupW2}>
            <label className={classes.formLabel}>Request ID *</label>
            <input
              className={inputClass}
              value={formData.request_id || ''}
              onChange={(e) => handleChange('request_id', e.target.value)}
              placeholder="Enter request ID..."
              readOnly={readOnly}
            />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Estimate ID *</label>
            <input
              className={inputClass}
              value={formData.estimate_id || ''}
              onChange={(e) => handleChange('estimate_id', e.target.value)}
              onBlur={readOnly ? undefined : onEstimateIdBlur}
              placeholder="Enter estimate ID..."
              maxLength={7}
              readOnly={readOnly}
            />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Slate Type *</label>
            <select
              className={selectClass}
              value={formData.slate_type || 'CADENT'}
              onChange={(e) => handleChange('slate_type', e.target.value)}
              disabled={readOnly}
            >
              <option value="CADENT">Cadent</option>
              <option value="CROSS">Cross</option>
              <option value="FLUIDITY">Fluidity</option>
            </select>
          </div>
          <div className={classes.formGroupFixedSm}>
            <label className={classes.formLabel}>Market Type *</label>
            <select
              className={selectClass}
              value={formData.market_type || 'LOCAL'}
              onChange={(e) => handleChange('market_type', e.target.value)}
              disabled={readOnly}
            >
              <option value="LOCAL">Local</option>
              <option value="NATIONAL">National</option>
            </select>
          </div>
        </div>
        <div className={classes.formRow}>
          <div className={classes.formGroupFixedMd}>
            <label className={classes.formLabel}>Inventory Source</label>
            <select
              className={selectClass}
              value={formData.inventory_source_id || ''}
              onChange={(e) => handleChange('inventory_source_id', e.target.value)}
              disabled={readOnly}
            >
              <option value="">Select...</option>
              {(inventorySources || []).map((src) => (
                <option key={src.id} value={src.id}>
                  {src.name}
                </option>
              ))}
            </select>
          </div>
          <div className={classes.formGroupFixedMd}>
            <label className={classes.formLabel}>Unit/Rotation *</label>
            <select
              className={selectClass}
              value={formData.unit_rotation || 'unit'}
              onChange={(e) => handleChange('unit_rotation', e.target.value)}
              disabled={readOnly}
            >
              <option value="unit">Unit</option>
              <option value="rotation">Rotation</option>
            </select>
          </div>
        </div>
        {showFlightWindow && (
          <div className={classes.formRow}>
            <div className={classes.formGroupFixedMd}>
              <label className={classes.formLabel}>Start Date</label>
              <div className={classes.flightDatePicker}>
                <SingleDatePicker
                  id="flight_start_date"
                  date={toMoment(formData.flight_start_date)}
                  onDateChange={handleStartDateChange}
                  focused={startDateFocused}
                  onFocusChange={({ focused }) => setStartDateFocused(focused)}
                  isDayBlocked={(day) => day.day() !== MONDAY}
                  isOutsideRange={() => false}
                  numberOfMonths={1}
                  displayFormat={ISO_FORMAT}
                  placeholder="Select Monday"
                  disabled={readOnly}
                  noBorder
                  block
                  small
                  hideKeyboardShortcutsPanel
                  withPortal
                />
              </div>
            </div>
            <div className={classes.formGroupFixedMd}>
              <label className={classes.formLabel}>End Date</label>
              <div className={classes.flightDatePicker}>
                <SingleDatePicker
                  id="flight_end_date"
                  date={toMoment(formData.flight_end_date)}
                  onDateChange={handleEndDateChange}
                  focused={endDateFocused}
                  onFocusChange={({ focused }) => setEndDateFocused(focused)}
                  isDayBlocked={(day) => day.day() !== SUNDAY}
                  isOutsideRange={() => false}
                  numberOfMonths={1}
                  displayFormat={ISO_FORMAT}
                  placeholder="Select Sunday"
                  disabled={readOnly}
                  noBorder
                  block
                  small
                  hideKeyboardShortcutsPanel
                  withPortal
                />
              </div>
            </div>
            <div className={classes.formGroupFixedSm}>
              <label className={classes.formLabel}>Number of Weeks</label>
              <input
                className={inputClass}
                type="number"
                min="1"
                step="1"
                value={formData.flight_weeks || ''}
                onChange={(e) => handleWeeksChange(e.target.value)}
                placeholder="Weeks"
                readOnly={readOnly}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};
